import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutDashboard, Save, Loader2 } from "lucide-react";
import {
  DASHBOARD_STAT_OPTIONS,
  parseDashboardStatsAll,
} from "@/lib/dashboardStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * @param {{
 *   title: string;
 *   description: string;
 *   selected: string[];
 *   onToggle: (key: string) => void;
 *   onSave: () => void;
 *   saving: boolean;
 *   testIdPrefix: string;
 * }} props
 */
const StatPicker = ({
  title,
  description,
  selected,
  onToggle,
  onSave,
  saving,
  testIdPrefix,
}) => (
  <Card data-testid={`${testIdPrefix}-panel`}>
    <CardHeader className="pb-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription className="mt-1">{description}</CardDescription>
        </div>
        <Button
          size="sm"
          className="gap-1.5 flex-shrink-0 h-9"
          onClick={onSave}
          disabled={saving}
          data-testid={`${testIdPrefix}-save`}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {DASHBOARD_STAT_OPTIONS.map((opt) => {
          const checked = selected.includes(opt.key);
          const id = `${testIdPrefix}-${opt.key}`;
          return (
            <label
              key={opt.key}
              htmlFor={id}
              className="flex items-start gap-3 rounded-lg border border-border px-3 py-3 cursor-pointer hover:bg-secondary/40 transition-colors"
              data-testid={`${testIdPrefix}-option-${opt.key}`}
            >
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={() => onToggle(opt.key)}
                className="mt-0.5"
                aria-label={opt.label}
              />
              <div className="min-w-0">
                <Label htmlFor={id} className="text-sm font-medium cursor-pointer">
                  {opt.label}
                </Label>
                {opt.description ? (
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {opt.description}
                  </p>
                ) : null}
              </div>
            </label>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        {selected.length} of {DASHBOARD_STAT_OPTIONS.length} cards selected
      </p>
    </CardContent>
  </Card>
);

export default function DashboardCardsSettings() {
  const [adminStats, setAdminStats] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [savingUsers, setSavingUsers] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/constants/dashboard-stats/all`);
        const parsed = parseDashboardStatsAll(res.data);
        setAdminStats(parsed.admin);
        setUserStats(parsed.users);
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.detail || "Failed to load dashboard card settings");
      }
      setLoading(false);
    };
    load();
  }, []);

  const toggleAdmin = (key) => {
    setAdminStats((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleUsers = (key) => {
    setUserStats((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleSaveAdmin = async () => {
    setSavingAdmin(true);
    try {
      await axios.put(`${API}/constants/dashboard-stats`, {
        allowed_dashboard_stats: adminStats,
      });
      toast.success("Admin dashboard cards updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save admin cards");
    }
    setSavingAdmin(false);
  };

  const handleSaveUsers = async () => {
    setSavingUsers(true);
    try {
      await axios.put(`${API}/constants/dashboard-stats/users`, {
        allowed_dashboard_stats: userStats,
      });
      toast.success("User dashboard cards updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save user cards");
    }
    setSavingUsers(false);
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-16 text-sm text-muted-foreground gap-2"
        data-testid="dashboard-cards-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading card settings…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dashboard-cards-settings-page">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
          <LayoutDashboard className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">
            Choose which stat cards appear on the dashboard for admins and for regular users.
          </p>
        </div>
      </div>

      <Tabs defaultValue="admin" className="w-full">
        <TabsList className="mb-4 w-full sm:w-auto grid grid-cols-2 sm:inline-flex h-10">
          <TabsTrigger value="admin" data-testid="tab-admin-cards" className="text-sm">
            Admin cards
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-user-cards" className="text-sm">
            User cards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="admin" className="mt-0">
          <StatPicker
            title="Admin dashboard cards"
            description="Shown when the signed-in role is an admin."
            selected={adminStats}
            onToggle={toggleAdmin}
            onSave={handleSaveAdmin}
            saving={savingAdmin}
            testIdPrefix="admin-cards"
          />
        </TabsContent>

        <TabsContent value="users" className="mt-0">
          <StatPicker
            title="User dashboard cards"
            description="Shown for non-admin roles on the dashboard."
            selected={userStats}
            onToggle={toggleUsers}
            onSave={handleSaveUsers}
            saving={savingUsers}
            testIdPrefix="user-cards"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
